import { AfterViewInit, ChangeDetectorRef, Directive, ElementRef, Host, Input, OnDestroy, Optional, Renderer2 } from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, takeUntil } from 'rxjs/operators';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { NzTreeSelectComponent } from 'ng-zorro-antd/tree-select';

@Directive({
  selector: '[appNzDynamicMaxTagCount]'
})
export class NzDynamicMaxTagCountDirective implements AfterViewInit, OnDestroy {
  @Input() appNzDynamicMaxTagCount: UntypedFormControl | AbstractControl;

  private readonly RESERVE_WIDTH = 180;
  private readonly DEBOUNCE_TIME = 100;
  private readonly unsubscribe$ = new Subject<void>();
  private resizeObserver: ResizeObserver | null = null;
  private cachedTags: Array<{ id: string; label: string; width: number }> = [];

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    @Optional() @Host() private nzSelect: NzSelectComponent | null,
    @Optional() @Host() private nzTreeSelect: NzTreeSelectComponent | null
  ) {}

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      const initialValue = this.appNzDynamicMaxTagCount.value || [];

      this.appNzDynamicMaxTagCount.valueChanges
        .pipe(
          debounceTime(this.DEBOUNCE_TIME),
          distinctUntilChanged(),
          startWith(initialValue),
          takeUntil(this.unsubscribe$)
        )
        .subscribe((selectedValues: string[]) => {
          const values = Array.isArray(selectedValues) ? selectedValues : [];
          this.calculateAndSetMaxTagCount(values, true);
        });

      const selectorEl = this.getSelectorElement();
      if (selectorEl) {
        this.setupResizeObserver(selectorEl);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private calculateAndSetMaxTagCount(selectedValues: string[], shouldUpdateCache: boolean): void {
    if (!selectedValues || selectedValues.length === 0) {
      return;
    }

    const selectorEl = this.getSelectorElement();
    if (!selectorEl) {
      return;
    }

    if (shouldUpdateCache) {
      const tagItems = this.getTagItems(selectorEl);
      this.updateCache(selectedValues, tagItems);
    }

    const containerWidth = selectorEl.offsetWidth;
    const offsetParent = selectorEl.offsetParent;
    if (containerWidth <= this.RESERVE_WIDTH || offsetParent === null) {
      return;
    }

    if (this.cachedTags.length === 0) {
      return;
    }

    const maxTagCount = this.getMaxTagCount(containerWidth, this.cachedTags);
    this.setMaxTagCount(maxTagCount >= selectedValues.length ? undefined : maxTagCount);
  }

  private setMaxTagCount(count?: number): void {
    if (this.nzSelect || this.nzTreeSelect) {
      this.applyMaxTagCount(this.nzSelect || this.nzTreeSelect, count);
      return;
    }

    this.renderer.setProperty(this.el.nativeElement, 'nzMaxTagCount', count);
  }

  private applyMaxTagCount(componentInstance: NzSelectComponent | NzTreeSelectComponent, count?: number): void {
    componentInstance.nzMaxTagCount = count;
    this.cdr.markForCheck();
  }

  private getSelectorElement(): HTMLElement | null {
    const hostEl = this.el.nativeElement as HTMLElement;
    if (this.nzTreeSelect) {
      const treeSelectSelector = hostEl.querySelector('.ant-select-selector') as HTMLElement | null;
      if (treeSelectSelector) {
        return treeSelectSelector;
      }
    }

    if (this.nzSelect) {
      const selectTopControl = hostEl.children[0] as HTMLElement | undefined;
      if (selectTopControl) {
        return selectTopControl;
      }
    }

    return null;
  }

  private setupResizeObserver(selectorEl: HTMLElement): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const width = (entry.target as HTMLElement).offsetWidth;
        if (width > this.RESERVE_WIDTH) {
          const currentValues = this.appNzDynamicMaxTagCount?.value || [];
          const values = Array.isArray(currentValues) ? currentValues : [];
          this.calculateAndSetMaxTagCount(values, false);
        }
      }
    });

    this.resizeObserver.observe(selectorEl);
  }

  private getMaxTagCount(containerWidth: number, cachedTags: Array<{ width: number }>): number {
    let availableWidth = containerWidth;
    let maxTagCount = 0;

    for (let index = 0; index < cachedTags.length; index++) {
      const itemWidth = cachedTags[index].width;
      if (availableWidth - itemWidth >= this.RESERVE_WIDTH) {
        availableWidth -= itemWidth;
        maxTagCount = index + 1;
      } else {
        break;
      }
    }

    return maxTagCount;
  }

  private updateCache(selectedValues: string[], tagItems: HTMLElement[]): void {
    const averageWidth = this.getAverageWidth(tagItems) || this.getAverageCachedWidth() || this.RESERVE_WIDTH;

    this.cachedTags = selectedValues.map((value, index) => {
      const existing = this.cachedTags[index];
      const itemEl = tagItems[index];
      const width = itemEl?.offsetWidth ?? 0;
      const label = itemEl ? this.getTagLabel(itemEl) : existing?.label || '';

      return {
        id: String(value ?? ''),
        label,
        width: width !== 0 ? width : averageWidth
      };
    });
  }

  private getTagItems(selectorEl: HTMLElement): HTMLElement[] {
    const items = Array.from(selectorEl.querySelectorAll('.ant-select-selection-item')) as HTMLElement[];
    return items.filter(item => !item.classList.contains('ant-select-selection-item-disabled'));
  }

  private getTagLabel(item: HTMLElement): string {
    return (item.textContent || '').trim();
  }

  private getAverageWidth(items: HTMLElement[]): number {
    const widths = items.map(item => item.offsetWidth).filter(width => width > 0);
    if (widths.length === 0) {
      return 0;
    }

    const total = widths.reduce((sum, width) => sum + width, 0);
    return total / widths.length;
  }

  private getAverageCachedWidth(): number {
    const widths = this.cachedTags.map(item => item.width).filter(width => width > 0);
    if (widths.length === 0) {
      return 0;
    }

    const total = widths.reduce((sum, width) => sum + width, 0);
    return total / widths.length;
  }
}
